import { useEffect, useMemo, useRef, useState } from "react";
import { dedupeTestimonials, pickNext } from "@/lib/testimonialPicker";

const MIN_GAP = 8;

// Cache Valley Consulting client testimonials, provided by Jonathan.
// `weight` = how many times more often this quote appears in rotation
// (bracketed high-impact quotes weight 4; standard quotes weight 1).
const TESTIMONIALS = [
  { quote: "He helped us hire an A player! Many more will come.",                                       author: "Lane",     company: "Repairs",                weight: 1 },
  { quote: "I quickly started finding people and improved significantly as a hiring manager.",         author: "Matt",     company: "Foods",                  weight: 1 },
  { quote: "Employees now run daily operations, leaving me focused on business growth.",               author: "Matt",     company: "Foods",                  weight: 1 },
  { quote: "Our managers became better identifiers and supporters of talent.",                         author: "Mike",     company: "Tech",                   weight: 1 },
  { quote: "I can grow the business without burning myself out.",                                      author: "Lane",     company: "Repairs",                weight: 1 },
  { quote: "I stopped working sixty-hour weeks.",                                                      author: "Aaron",    company: "Manufacturing",          weight: 1 },
  { quote: "We calculated that we got 83x back in ROI. 83x!!",                                         author: "Aaron",    company: "Manufacturing",          weight: 4 },
  { quote: "The bank loved my business plan and approved my loan!",                                    author: "KC",       company: "Hollywood Props",        weight: 1 },
  { quote: "We're still early, but the results are already undeniable! LET'S GO!!",                    author: "Ben",      company: "Services",               weight: 4 },
  { quote: "Every meeting leaves me feeling that I have made great progress.",                         author: "NGP",      company: "Repairs",                weight: 1 },
  { quote: "All of the guidance provided is immediately applicable to our business.",                  author: "Casey",    company: "Manufacturing",          weight: 1 },
  { quote: "He diagnosed our challenges, clarified solutions, and motivated real change.",             author: "Chris",    company: "Tech",                   weight: 1 },
  { quote: "Jonathan made business practices easy to follow and put into practice.",                   author: "Jason",    company: "Manufacturing",          weight: 1 },
  { quote: "Epic gave me concrete action items my MBA never provided.",                                author: "Phil",     company: "Foods",                  weight: 4 },
  { quote: "I discovered who I am and who fits our culture.",                                          author: "Justin",   company: "Restoration",            weight: 1 },
  { quote: "Prioritize these counterintuitive steps to scale. Level up and be amazed!",                author: "David",    company: "Pet Health",             weight: 1 },
  { quote: "Grow and scale your business without the risky guesswork.",                                author: "Alex",     company: "Mergers & Acquisitions", weight: 1 },
  { quote: "His insights help individuals and companies make important improvements.",                 author: "Mike P.",  company: "Business Services",      weight: 1 },
  { quote: "Your business will profit from inside-out growth, if you can get in!",                     author: "Aaron T.", company: "Medical",                weight: 1 },
  { quote: "I understand management and the business game so much better.",                           author: "BC",       company: "Robotics",               weight: 1 },
  { quote: "CVC has helped our company grow very much in three years!",                                author: "Jared",    company: "Machining",              weight: 4 },
  { quote: "We found many ways to improve the structure needed for growth.",                           author: "Calvin",   company: "Fabrication",            weight: 1 },
];

/**
 * Slender, non-intrusive fade-based testimonial ticker.
 * One quote at a time, cross-fades every ~5.5s. Sits just under the nav bar.
 * Featured quotes (weight=4) are ~4× likelier; no quote repeats within MIN_GAP rotations.
 * Also accepts CMS-driven `content.testimonial_N_*` keys as fallback if provided.
 */
export default function TestimonialTicker({ content = {} }) {
  const items = useMemo(() => {
    // Legacy CMS-driven testimonials (populated via admin > Landing Copy)
    const fromCms = Array.from({ length: 12 }, (_, idx) => idx + 1)
      .map((i) => ({
        quote:   content[`testimonial_${i}_quote`]   || "",
        author:  content[`testimonial_${i}_author`]  || "",
        company: content[`testimonial_${i}_company`] || "",
        weight: 1,
      }))
      .filter((t) => t.quote.trim().length > 0);
    // Merge both sources (CMS defaults to weight 1), dropping duplicate quotes.
    return dedupeTestimonials([...fromCms, ...TESTIMONIALS]);
  }, [content]);

  const [idx, setIdx] = useState(0);
  const history = useRef([]);

  // Fresh random start whenever the pool changes, carrying over recently shown quotes.
  useEffect(() => {
    if (items.length === 0) return;
    const recentQuotes = new Set(history.current.map((q) => q.quote));
    const carried = items.map((t, i) => (recentQuotes.has(t.quote) ? i : -1)).filter((i) => i >= 0);
    const first = pickNext(items, carried, MIN_GAP);
    history.current = [items[first]];
    setIdx(first);
  }, [items]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (items.length <= 1) return;
    let mounted = true;
    const fadeOutMs = 600;
    const holdMs = 4900;
    let outTimer, inTimer;
    const tick = () => {
      if (!mounted) return;
      setVisible(false);
      outTimer = setTimeout(() => {
        if (!mounted) return;
        const recent = history.current.map((q) => items.indexOf(q));
        const next = pickNext(items, recent, MIN_GAP);
        history.current = [...history.current.slice(-MIN_GAP), items[next]];
        setIdx(next);
        setVisible(true);
        inTimer = setTimeout(tick, holdMs);
      }, fadeOutMs);
    };
    inTimer = setTimeout(tick, holdMs);
    return () => {
      mounted = false;
      clearTimeout(outTimer);
      clearTimeout(inTimer);
    };
  }, [items]);

  if (items.length === 0) return null;
  const t = items[idx] || items[0];

  return (
    <div
      data-testid="testimonial-ticker"
      className="bg-cvc-cream/80 border-b border-cvc-border"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2.5 flex items-center justify-center min-h-[44px]">
        <p
          data-testid="testimonial-ticker-item"
          className="text-sm md:text-[15px] text-cvc-ink leading-snug text-center transition-opacity duration-500"
          style={{ opacity: visible ? 1 : 0 }}
        >
          <span className="font-heading italic text-cvc-navy">&ldquo;{t.quote}&rdquo;</span>
          {(t.author || t.company) && (
            <span className="font-body text-cvc-ink-soft text-xs md:text-sm ml-2">
              {/* Strip any leading em/en/hyphen dashes from the author field so
                  the template's own em-dash isn't doubled up (some legacy CMS
                  entries include a leading dash in the author cell). */}
              — {String(t.author || "").replace(/^[\s\u2014\u2013-]+/, "")}
              {t.author && t.company ? ", " : ""}
              {t.company && <span className="uppercase tracking-wider text-[11px] text-cvc-red">{t.company}</span>}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
